import React, { useEffect, useState } from 'react'
import DataTable from 'react-data-table-component'
import { useDispatch } from 'react-redux'
import Pagination from '../../../../components/UI/Pagination/Pagination'
import QuestionListSkeleton from '../../../../components/UI/Skeletons/QuestionListSkeleton/QuestionListSkeletons'
import { getUserFeedbacks } from '../../../../store/dashboard/actions'
import {
  getAllButtonOptions,
  getAllIntents,
  getAllUserGroups,
  updateUserFeedback,
} from '../../../../store/questions/actions'
import { ChartTableSortIcon } from '../../../Knowledge/AllKnowledge'
import KnowledgeCreateFromScratch from '../../../Knowledge/KnowledgeCreateFromScratch/KnowledgeCreateFromScratch'
import { customStyles } from '../MatchedUnmatchedQuestions/MatchedUnmatchedUtils'
import AddToExistingIntent from '../MatchedUnmatchedQuestions/partials/AddToExisting'
import { conditionalRowStyles, userFeedbackColumns } from './reportsUtils'
import cx from 'classnames'
import { getStickerSets } from '../../../../store/common/actions'
import { getAllUserGroupOptions } from '../../../../store/notifications/actions'

import { useTranslation } from 'react-i18next'
import ExportReportShared from '../../../../components/UI/ExportReportShared/ExportReportShared'
import DateSelect from '@/components/shared/DateSelect'

const UserFeedback = () => {
  const { t } = useTranslation('translation')
  const dispatch = useDispatch()
  const [perPage, setPerPage] = useState(25)
  const [existingRecord, setExistingRecord] = useState(null)
  const [matchedRecords, setMatchedRecords] = useState([])
  const [matchedRecordsPagination, setMatchedRecordsPagination] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [filters, setFilters] = useState({
    page: null,
    page_size: null,
  })
  const [showTagsModal, setShowTagsModal] = useState(false)

  const [dateFilters, setDateFilters] = useState({
    fromDate: null,
    toDate: null,
  })

  useEffect(() => {
    dispatch(getAllUserGroups())
    dispatch(getStickerSets())
    dispatch(getAllUserGroupOptions())
    dispatch(getAllButtonOptions())
    dispatch(getAllIntents())
  }, [])

  useEffect(() => {
    getMatchedFunctions()
  }, [filters, dateFilters])

  const i18nName = (cols: any) => {
    if (cols && cols.length > 0) {
      return cols.map((col: any) => ({ ...col, name: t(col.name) }))
    }
    return cols
  }

  const getMatchedFunctions = async () => {
    try {
      setLoading(true)
      const matchedQuestions: any = await getUserFeedbacks({
        from_date: dateFilters.fromDate,
        to_date: dateFilters.toDate,
        page: currentPage,
        page_size: perPage,
      })
      setMatchedRecords(matchedQuestions.rows)
      setMatchedRecordsPagination(matchedQuestions.pagination)
      setLoading(false)
    } catch (error: any) {
      setLoading(false)
    }
  }

  /**
   *
   * @param page Pagination on change of page
   */
  const handlePageChange = async (page: number) => {
    setCurrentPage(page)
    setFilters({
      ...filters,
      page: page,
    })
  }

  /**
   * Used to paginate data based on per page and page number
   * @param newPerPage
   * @param page
   */
  const handlePerRowsChange = async (newPerPage: number, page: number) => {
    await setPerPage(newPerPage)
    await setCurrentPage(page)
    await setFilters({
      ...filters,
      page: page,
      page_size: newPerPage,
    })
  }

  const addToExistingIntent = (e: any) => {
    setExistingRecord(e)
    setShowTagsModal(!showTagsModal)
  }

  const confirmAddToExistingIntent = (e: any) => {
    setShowTagsModal(!showTagsModal)
    editKnowledgehandler({
      _id: e._id,
      existingRecord: {
        ...existingRecord,
        question: existingRecord.question_text,
        answer: existingRecord.reply_text,
      },
    })
  }

  const acknowledgeConfirmed = async (selectedRow: any) => {
    await dispatch(updateUserFeedback(selectedRow))
  }

  const [editMode, setEditMode] = useState(false)
  const [selectedIntent, setSelectedIntent] = useState(null)
  const editKnowledgehandler = (intent: any) => {
    const editModeCheck = !editMode
    setSelectedIntent(intent)
    setEditMode(editModeCheck)
  }

  const resetEditKnowledgehandler = (action: any) => {
    const editModeCheck = !editMode
    setSelectedIntent(null)
    setEditMode(editModeCheck)
  }

  return (
    <div className="tw-border tw-rounded-2xl tw-shadow-md tw-p-8">
      <AddToExistingIntent
        onHide={addToExistingIntent}
        show={showTagsModal}
        confirmhandler={confirmAddToExistingIntent}
        selectedTags={[]}
      />

      <div className="welcome-subtext d-flex flex-md-nowrap flex-wrap mb-4">
        <h2>{t('Feedback')}</h2>
        <div className="d-flex flex-wrap sugested-next-ques"></div>
        <div className="d-flex flex-wrap sugested-next-ques ml-auto">
          <DateSelect
            onChange={({ fromDate, toDate }) => {
              setDateFilters({ fromDate, toDate })
            }}
          />
          <ExportReportShared
            name={'feedbacks'}
            filtersData={filters}
            removedKeyFromObject={'page, page_size'}
          />
        </div>
      </div>

      <div
        className={cx('right-animate', {
          show: editMode,
        })}
      >
        {editMode && (
          <KnowledgeCreateFromScratch
            resetEditKnowledgehandler={resetEditKnowledgehandler}
            selectedIntent={selectedIntent}
            editMode={editMode}
          />
        )}
      </div>

      <div>
        <DataTable
          className="table-wraper bot-exp-user-feedback-min-height"
          noContextMenu
          noHeader
          fixedHeader
          fixedHeaderScrollHeight="700px"
          columns={i18nName(
            userFeedbackColumns({
              editKnowledgehandler,
              addToExistingIntent: addToExistingIntent,
              acknowledgeConfirmed,
            }),
          )}
          data={matchedRecords ? matchedRecords : []}
          customStyles={customStyles}
          sortIcon={<ChartTableSortIcon />}
          paginationPerPage={perPage}
          paginationDefaultPage={1}
          paginationResetDefaultPage={false}
          paginationTotalRows={0}
          onChangeRowsPerPage={handlePerRowsChange}
          onChangePage={handlePageChange}
          progressPending={loading} //
          progressComponent={
            <div style={{ padding: 10, width: '100%' }}>
              <QuestionListSkeleton count={12} />
            </div>
          }
          defaultSortAsc={false}
          conditionalRowStyles={conditionalRowStyles}
          noDataComponent={t('There are no records to display')}
          // onSort={onSortHandler}
        />

        {/* -----------------  Pagination   --------------------------- */}
        {matchedRecords &&
          matchedRecords.length > 0 &&
          matchedRecordsPagination && (
            <Pagination
              currentPage={currentPage}
              pagination={matchedRecordsPagination}
              paginationPerPage={perPage}
              pageChange={handlePerRowsChange}
              onChangePage={handlePageChange}
            />
          )}
      </div>
    </div>
  )
}

export default UserFeedback
